import { memo, useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { COMMERCIAL_INVOICE_STATUS_LABELS } from "./CommercialInvoiceStatusBadge";
import type { CommercialInvoiceListFilters } from "../hooks/useCommercialInvoiceList";
import type { CommercialInvoiceStatus } from "../types/commercial-invoice.types";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const STATUS_OPTIONS: CommercialInvoiceStatus[] = [
  "ISSUED",
  "PENDING",
  "ERROR",
  "CANCELED",
];

interface CommercialInvoiceFiltersProps {
  filters: CommercialInvoiceListFilters;
  loading: boolean;
  onApply: (filters: CommercialInvoiceListFilters) => void;
  onClear: () => void;
}

export const CommercialInvoiceFilters = memo(function CommercialInvoiceFilters({
  filters,
  loading,
  onApply,
  onClear,
}: CommercialInvoiceFiltersProps) {
  const [draft, setDraft] = useState<CommercialInvoiceListFilters>(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Filtros de Busca</CardTitle>
        <CardDescription>
          O período considera a data de criação da nota — notas indefinidas ou
          rejeitadas não têm data de autorização.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="commercial-invoice-filter-date">
              Data de criação
            </Label>
            <Input
              id="commercial-invoice-filter-date"
              type="date"
              value={draft.date ?? ""}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  date: e.target.value || undefined,
                }))
              }
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="commercial-invoice-filter-status">Status</Label>
            <select
              id="commercial-invoice-filter-status"
              className={SELECT_CLASSES}
              value={draft.status ?? ""}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  status:
                    e.target.value === ""
                      ? undefined
                      : (e.target.value as CommercialInvoiceStatus),
                }))
              }
              disabled={loading}
            >
              <option value="">Todos</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {COMMERCIAL_INVOICE_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 lg:col-span-1 lg:self-end">
            <div className="flex flex-wrap gap-2 md:justify-end lg:justify-start">
              <Button
                variant="outline"
                onClick={onClear}
                disabled={loading}
                className="gap-2"
              >
                <X className="w-4 h-4" />
                Limpar
              </Button>
              <Button
                onClick={() => onApply(draft)}
                disabled={loading}
                className="gap-2"
              >
                <Search className="w-4 h-4" />
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
